// Mur juridique : QUE montrer, à partir de ce que le serveur a dit.
//
// Trois entrées possibles, une seule sortie :
//   - `legalGate`      : le dernier refus 451 (réactif — le serveur VIENT de
//                        refuser une action) ; il prime toujours ;
//   - `legalStatus`    : le statut lu à la connexion d'un UTILISATEUR ;
//   - `assoLegalStatus`: le statut lu à la connexion d'une ASSOCIATION.
//
// Depuis #2377, un statut en retard n'est plus forcément BLOQUANT : pendant la
// fenêtre de grâce (avant `effective_at`), le serveur rend `grace: true` et
// `blocking: false`. Dans ce cas on montre un BANDEAU, pas le mur.
//
// Vieux serveur (sans `blocking`) → on bloque comme avant : `blocking !== false`
// est volontairement écrit ainsi pour qu'un champ ABSENT vaille « bloquant ».
export const estBloquant = (s) => !!s && !s.up_to_date && s.blocking !== false;
export const estEnGrace = (s) => !!s && !s.up_to_date && s.grace === true && s.blocking === false;

const _cible = (s, accountType) => ({
  needsAcceptance: Array.isArray(s?.needs_acceptance) ? s.needs_acceptance : [],
  accountType,
});

/**
 * @returns {{ mur: object|null, grace: object|null }}
 *   `mur`   → monter la modale bloquante (needsAcceptance, accountType)
 *   `grace` → afficher le bandeau (needsAcceptance, accountType, effectiveAt)
 *   Les deux sont exclusifs : un mur cache toujours le bandeau.
 */
export const etatMurJuridique = ({ user, legalStatus, assoAccount, assoLegalStatus, legalGate }) => {
  const asso = assoAccount ? assoLegalStatus : null;
  const usr = user ? legalStatus : null;
  if (legalGate) return { mur: legalGate, grace: null };
  if (estBloquant(asso)) return { mur: _cible(asso, "association"), grace: null };
  if (estBloquant(usr)) return { mur: _cible(usr, "user"), grace: null };
  if (estEnGrace(asso)) return { mur: null, grace: { ..._cible(asso, "association"), effectiveAt: asso.effective_at } };
  if (estEnGrace(usr)) return { mur: null, grace: { ..._cible(usr, "user"), effectiveAt: usr.effective_at } };
  return { mur: null, grace: null };
};

/** « 20 septembre 2026 » — vide si la date est illisible (jamais « Invalid Date »). */
export const formatDateEntreeEnVigueur = (iso) => {
  try {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch (_) {
    return "";
  }
};

/**
 * Chemins des DOCUMENTS juridiques eux-memes — source UNIQUE.
 *
 * LE DEFAUT QUE CETTE CONSTANTE FERME (signale le 23/09/2026 par Rachel Madec,
 * presidente d'ABC MucO, la premiere association a nous l'ecrire) :
 *
 *   « Il ne m'est pas possible de lire les documents listes avant d'accepter les
 *     conditions generales. Une fenetre s'ouvre et se referme aussitot. »
 *
 * Le mur juridique (`LegalUpdateModal`) etait monte dans `App.js` AVANT
 * `<Routes>`, donc sur TOUTES les routes, et `etatMurJuridique` n'avait aucune
 * notion de chemin. Ouvrir un document dans un nouvel onglet rechargeait donc
 * l'application, et le mur se remontait PAR-DESSUS le texte, en plein ecran,
 * avec le scroll du corps verrouille. L'utilisatrice voyait l'onglet s'ouvrir
 * puis « se refermer » — c'etait la meme modale qui se rabattait dessus.
 *
 * IMPASSE FERMEE : il fallait accepter pour lire, et lire pour accepter.
 *
 * Ces quatre chemins sont donc EXEMPTES du mur. Ce n'est pas un affaiblissement :
 * le mur existe pour empecher d'UTILISER la plateforme sans avoir accepte, et
 * lire le contrat qu'on nous demande de signer n'est pas un usage — c'est la
 * condition prealable du consentement. Un consentement donne sans possibilite
 * de lecture n'est pas un consentement.
 *
 * SOURCE UNIQUE, volontairement : `LegalUpdateModal` construit ses liens depuis
 * cette meme table. Deux listes de chemins finiraient par diverger, et le jour
 * ou elles divergent l'impasse revient — en silence, comme la premiere fois.
 */
export const CHEMINS_DOCUMENTS_JURIDIQUES = Object.freeze({
  cguUtilisateur: "/cgu",
  cguAssociation: "/association/cgu",
  cgv: "/cgv",
  mandat: "/mandat-collecte",
  partenariat: "/contrat-partenariat",
});

/** Vrai si `pathname` est la page d'un document juridique (lecture seule). */
export const estPageDocumentJuridique = (pathname) => {
  const p = String(pathname || "").replace(/\/+$/, "") || "/";
  return Object.values(CHEMINS_DOCUMENTS_JURIDIQUES).includes(p);
};

/**
 * Décide s'il faut monter le MUR juridique, en tenant compte du CHEMIN.
 *
 * POURQUOI CETTE FONCTION EXISTE, plutôt qu'un ternaire dans `App.js` : la
 * première version du correctif vivait dans le composant, et sa garde ne
 * pouvait vérifier que du TEXTE. Mutation jouée le 23/09/2026 — retirer
 * l'exemption — : la garde est restée VERTE. Un test qui ne détecte pas le
 * retrait de ce qu'il protège ne protège rien.
 *
 * La décision est donc ici, pure et testable par son COMPORTEMENT : on lui
 * donne un état et un chemin, elle rend le mur ou `null`. La mutation devient
 * alors impossible à rater.
 */
export const murAAfficher = ({ mur, grace, graceOuverte, pathname }) => {
  // Sur la page d'un document, JAMAIS de mur : lire le contrat qu'on nous
  // demande de signer est la condition du consentement, pas un usage à bloquer.
  if (estPageDocumentJuridique(pathname)) return null;
  if (mur) return mur;
  if (graceOuverte && grace) return { ...grace, volontaire: true };
  return null;
};
